/*
    Navigation.tsx renders the side navigation bar
*/
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    CustomDetailEvent,
    SideNavigation,
} from '@amzn/awsui-components-react-v2';
import { useHistory } from 'react-router-dom';
import {
    useGrimsbyNav,
    useGrimsbyNavActions,
} from '../../context/grimsbyNavigation';
import { NavItem } from '../../interfaces/navigation';
import { selectFeatures } from '../../store/slices/featureSlice';
import { selectRoles, selectUser } from '../../store/slices/userSlice';

export const headerData: SideNavigation.Header = { text: 'Home', href: '/' };

// Path constants for navigation routing
export const PATHS = {
    ACTIVITIES: '/activities',
    ACTIVITY_GROUPS: '/activities/group',
    ACTIVITY_GROUPS_LIST: '/activities/group/list',
    ADMIN: '/admin',
    ADMIN_USERS: '/admin/user-management/users',
    ADMIN_COURSES: '/admin/business-data/courses',
    EXPORT_INVOICE_DATA: '/activities/clipper-export',
    INSTRUCTOR_SCHEDULE: '/activities/instructors',
    SFDC_QUEUE: '/activities/sfdc-queue',
    SFDC_OPPORTUNITY: '/activities/opportunity',
};

const Navigation = () => {
    const [active, setActive] = useState('');
    const { navData } = useGrimsbyNav();
    const { getSideNavItems } = useGrimsbyNavActions();
    const history = useHistory();
    const roles = useSelector(selectRoles);
    const featureFlags = useSelector(selectFeatures);
    const user = useSelector(selectUser);
    const [items, setItems] = useState<Omit<NavItem, 'href'>[]>([]);

    // Function to determine the correct active navigation path for hierarchical navigation
    const getActiveNavigationPath = (currentPath: string): string => {
        // SMT - Export Invoice Data
        if (currentPath.startsWith(PATHS.EXPORT_INVOICE_DATA)) {
            return PATHS.EXPORT_INVOICE_DATA;
        }

        // SMT - Instructor Schedule
        if (currentPath.startsWith(PATHS.INSTRUCTOR_SCHEDULE)) {
            return PATHS.INSTRUCTOR_SCHEDULE;
        }

        // SMT - SFDC Queue and SFDC Opportunity
        if (currentPath.startsWith(PATHS.SFDC_QUEUE)) {
            return PATHS.SFDC_QUEUE;
        }

        // SMT - SFDC Opportunity (activities created from SFDC Queue)
        if (currentPath.startsWith(PATHS.SFDC_OPPORTUNITY)) {
            return PATHS.SFDC_QUEUE;
        }

        // SMT - Activities (excluding activity groups and the three specific sections above)
        if (
            currentPath.startsWith(PATHS.ACTIVITIES) &&
            !currentPath.startsWith(PATHS.ACTIVITY_GROUPS) &&
            !currentPath.startsWith(PATHS.EXPORT_INVOICE_DATA) &&
            !currentPath.startsWith(PATHS.INSTRUCTOR_SCHEDULE) &&
            !currentPath.startsWith(PATHS.SFDC_QUEUE)
        ) {
            return PATHS.ACTIVITIES;
        }

        // SMT - Activity Groups
        if (currentPath.startsWith(PATHS.ACTIVITY_GROUPS)) {
            return PATHS.ACTIVITY_GROUPS_LIST;
        }

        // Admin - Base admin path should highlight Users (the default landing page)
        if (currentPath === PATHS.ADMIN) {
            return PATHS.ADMIN_USERS;
        }

        // Admin - Users
        if (currentPath.startsWith(PATHS.ADMIN_USERS)) {
            return PATHS.ADMIN_USERS;
        }

        // Admin - Courses
        if (currentPath.startsWith(PATHS.ADMIN_COURSES)) {
            return PATHS.ADMIN_COURSES;
        }

        // All other paths remain unchanged
        return currentPath;
    };

    useEffect(() => {
        const currentPath = history.location.pathname;
        setActive(getActiveNavigationPath(currentPath));

        return history.listen((location) => {
            setActive(getActiveNavigationPath(location.pathname));
        });
    }, [history]);

    useEffect(() => {
        if (featureFlags) {
            setItems(
                getSideNavItems(
                    roles,
                    featureFlags?.features,
                    user?.profile.alias,
                ),
            );
        }
    }, [navData.navItems, roles, getSideNavItems, featureFlags, user]);

    return (
        <>
            {navData.navItems?.length && (
                <SideNavigation
                    data-testid="side_nav"
                    header={headerData}
                    items={items}
                    activeHref={active}
                    onFollow={(
                        event: CustomDetailEvent<SideNavigation.FollowDetail>,
                    ) => {
                        if (event.detail.external) return;
                        event.preventDefault();
                        history.push(event.detail.href);
                    }}
                />
            )}
        </>
    );
};

export default Navigation;
